import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, Link } from "react-router-dom";
import { ArrowUpLongIcon, Body2, Button, InfoBox, SpinnerLoader } from "@activecollab/components";
import ExternalEmptySlate from "~/components/EmptySlate/ExternalEmptySlate";
import { useAuth } from "~/components/Hooks/useAuth";
import { showErrorToast } from "~/components/Toast";
import PublicLayout from "~/pages/Layouts/PublicLayout";
import { usePublicPaymentStripe3dsProcessing } from "~/repository/usePublicPayments";
import localizationService from "~/services/LocalizationService";
import Angie from "~/utils/angie";
import { fetch } from "~/utils/fetch";
import { resolvePublicInvoiceLink } from "~/utils/utils";

export const PublicPaymentCreditCard3dsProcessing = () => {
  const { t } = useTranslation();
  const { loading } = useAuth();
  const [searchParams] = useSearchParams();
  const { mutate: completeStripe3ds, isError, isSuccess } = usePublicPaymentStripe3dsProcessing();
  const [finished, setIsFinished] = useState(false);
  const [response, setResponse] = useState(null);
  const [showEmptySlate, setShowEmptySlate] = useState(false);
  const [backResponse, setBackResponse] = useState(null);
  useEffect(() => {
    if (!searchParams.get("payment_intent")) {
      return setShowEmptySlate(true);
    }

    if (searchParams.get("payment_intent") && !loading) {
      completeStripe3ds(
        { payment_intent_id: searchParams.get("payment_intent") },
        {
          onSuccess: (r) => {
            setResponse(r);
          },
          onError: (e) => {
            if (e?.message) {
              Angie.logger.error("Error in payment", {
                error: e?.message,
              });
            }
            showErrorToast("Failed to process payment");
          },
          onSettled: () => {
            fetch({
              url: `${Angie.api_url}/public_payments/back-url?payment_intent_id=${searchParams.get("payment_intent")}`,
            }).then((r) => {
              setBackResponse(r);
              if (r?.language) {
                localizationService.configure({ language_id: r?.language?.id }, [r?.language], true);
              }
            });
            setIsFinished(true);
          },
        }
      );
    }
  }, [completeStripe3ds, loading, searchParams]);

  if (showEmptySlate) {
    return (
      <PublicLayout
        title={t("Not Found")}
        shouldShowTerms={false}
      >
        <ExternalEmptySlate isLoggedIn={false} />
      </PublicLayout>
    );
  }

  return finished ? (
    <PublicLayout
      shouldShowLogo
      paperClassName="tw-flex tw-flex-col tw-items-center tw-p-8 tw-w-[360px]"
      bodyClassName="tw-flex tw-items-center tw-justify-center"
    >
      {isSuccess && finished && response?.single?.status === "paid" ? (
        <>
          <InfoBox
            type="success"
            title={t("Payment Completed!")}
            showIcon
          >
            <Body2>
              {t("Your payment of {{amount}} {{currencyCode}} has been successfully completed.", {
                amount: response?.single?.amount,
                currencyCode: backResponse?.currency_code,
              })}
            </Body2>
          </InfoBox>
          <Link to={resolvePublicInvoiceLink(response?.single?.back_url)}>
            <Button
              variant="text colored"
              className="tw-mt-6"
            >
              {t("Continue To Invoice")}
              <ArrowUpLongIcon className="tw-rotate-90" />
            </Button>
          </Link>
        </>
      ) : null}
      {isSuccess && finished && response?.single?.status === "canceled" ? (
        <>
          <InfoBox
            type="note"
            title={t("Payment Canceled")}
            showIcon
          >
            <Body2>{t("You have canceled your payment.")}</Body2>
          </InfoBox>
          <Link to={resolvePublicInvoiceLink(response?.single?.back_url)}>
            <Button
              variant="text colored"
              className="tw-mt-6"
            >
              <ArrowUpLongIcon className="-tw-rotate-90" />
              {t("Back To Invoice")}
            </Button>
          </Link>
        </>
      ) : null}
      {isError ? (
        <>
          <InfoBox
            type="warning"
            title={t("Payment Unsuccessful")}
            showIcon
          >
            <Body2>
              {t("Your payment has not been charged.")}
              <>
                <br />
                <br />
                {t("Please try again, by opening the provided link.")}
              </>
            </Body2>
          </InfoBox>
        </>
      ) : null}
    </PublicLayout>
  ) : (
    <PublicLayout
      shouldShowLogo
      paperClassName="tw-flex tw-flex-col tw-items-center tw-p-8 tw-w-[360px]"
      bodyClassName="tw-flex tw-items-center tw-justify-center"
    >
      <SpinnerLoader
        radius={12}
        stroke={2}
        data-testid="spinner-loader"
      />
    </PublicLayout>
  );
};

export default PublicPaymentCreditCard3dsProcessing;
